import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useDietContext } from "@/contexts/DietContext";

// Funzioni di calcolo
function calculateBMR(weight: number, height: number, age: number, sex: string): number {
    const base = 10 * weight + 6.25 * height - 5 * age;
    return sex === "male" ? base + 5 : base - 161;
}

function calculateTDEE(bmr: number, lifestyle: string): number {
    const multipliers = {
        sedentary: 1.2,
        normal: 1.375,
        active: 1.55,
        athletic: 1.725,
    };
    return Math.round(bmr * multipliers[lifestyle as keyof typeof multipliers]);
}

function calculateDietCalories(tdee: number, target: string): number {
    switch (target) {
        case "fastWeightLoss":
            return Math.round(tdee * 0.75);
        case "normalWeightLoss":
            return Math.round(tdee * 0.85);
        case "slowWeightLoss":
            return Math.round(tdee * 0.9);
        case "maintenance":
            return tdee;
        case "lightMuscleGain":
            return Math.round(tdee * 1.1);
        case "normalMuscleGain":
            return Math.round(tdee * 1.15);
        case "fastMuscleGain":
            return Math.round(tdee * 1.25);
        default:
            return tdee;
    }
}

export const useDietForm = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { formData, setFormData } = useDietContext();

    const calculateCalories = () => {
        const weight = parseFloat(formData.weight);
        const height = parseFloat(formData.height);
        const age = parseInt(formData.age);
        const sex = formData.sex;
        const lifestyle = formData.lifestyle;
        const target = formData.target;

        if (isNaN(weight) || isNaN(height) || isNaN(age)) {
            toast({
                title: "Invalid Input",
                description: "Please enter valid numeric values for weight, height, and age.",
                variant: "destructive",
            });
            return;
        }

        // Calcoli
        const bmr = calculateBMR(weight, height, age, sex);
        const tdee = calculateTDEE(bmr, lifestyle);
        const calories = calculateDietCalories(tdee, target);

        // Aggiorna formData con i risultati
        setFormData((prev) => ({
            ...prev,
            bmr,
            tdee,
            calories,
        }));

        toast({
            title: t("buildDiet.toasts.calculate.title"),
            description: t("buildDiet.toasts.calculate.success"),
        });
    };

    return {
        calculateCalories,
    };
};
